import {ref, watch, onMounted, nextTick} from '@vue/composition-api'

const STORAGE_KEY = 'assetListFilters'

// Estado reativo global dos filtros para compartilhar entre instâncias
const globalFilters = {
    searchQuery: ref(''),
    searchOwner: ref(''),
    customerId: ref(''),
    chosenStatus: ref([]),
    showFilters: ref(false),
    assetTypeId: ref(null),
    assetBrandId: ref(null),
    assetGroupId: ref(null),
    perPage: ref(10),
    sortBy: ref('workstatus'),
    sortDesc: ref(false)
}

export default function useAssetFilters() {

    // Função para carregar filtros do localStorage
    const loadFiltersFromStorage = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY)
            if (stored) {
                const filters = JSON.parse(stored)

                // Aplicar filtros salvos aos refs globais
                globalFilters.searchQuery.value = filters.searchQuery || ''
                globalFilters.searchOwner.value = filters.searchOwner || ''
                globalFilters.customerId.value = filters.customerId || ''
                globalFilters.chosenStatus.value = filters.chosenStatus || []
                globalFilters.showFilters.value = filters.showFilters || false
                globalFilters.assetTypeId.value = filters.assetTypeId || null
                globalFilters.assetBrandId.value = filters.assetBrandId || null
                globalFilters.assetGroupId.value = filters.assetGroupId || null

                globalFilters.perPage.value = filters.perPage || 10
                globalFilters.sortBy.value = filters.sortBy || 'workstatus'
                globalFilters.sortDesc.value = filters.sortDesc || false

            }
        } catch (_) {}
    }

    // Hidratar antes do primeiro render para o b-table não buscar com defaults e depois refazer após onMounted
    if (typeof window !== 'undefined') {
        loadFiltersFromStorage()
    }

    // Função para salvar filtros no localStorage
    const saveFiltersToStorage = () => {
        try {
            const filters = {
                searchQuery: globalFilters.searchQuery.value,
                searchOwner: globalFilters.searchOwner.value,
                customerId: globalFilters.customerId.value,
                chosenStatus: globalFilters.chosenStatus.value,
                showFilters: globalFilters.showFilters.value,
                assetTypeId: globalFilters.assetTypeId.value,
                assetBrandId: globalFilters.assetBrandId.value,
                assetGroupId: globalFilters.assetGroupId.value,
                perPage: globalFilters.perPage.value,
                sortBy: globalFilters.sortBy.value,
                sortDesc: globalFilters.sortDesc.value
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
        } catch (_) {}
    }

    // Função para limpar filtros
    const clearFilters = () => {
        globalFilters.searchQuery.value = ''
        globalFilters.searchOwner.value = ''
        globalFilters.customerId.value = ''
        globalFilters.chosenStatus.value = []
        globalFilters.assetTypeId.value = null
        globalFilters.assetBrandId.value = null
        globalFilters.assetGroupId.value = null
        // Não resetar showFilters para manter a interface consistente
        // globalFilters.showFilters.value = false
        globalFilters.perPage.value = 10
        globalFilters.sortBy.value = 'workstatus'
        globalFilters.sortDesc.value = false

        // Salvar o estado limpo no localStorage
        saveFiltersToStorage()
    }

    // Função para atualizar searchOwner (para ser chamada do componente)
    const updateSearchOwner = (value) => {
        globalFilters.searchOwner.value = value
        // Salvar imediatamente após atualizar
        nextTick(() => {
            saveFiltersToStorage()
        })
    }

    // Função para atualizar customerId (para ser chamada do componente)
    const updateCustomerId = (value) => {
        globalFilters.customerId.value = value
        // Salvar imediatamente após atualizar
        nextTick(() => {
            saveFiltersToStorage()
        })
    }

    // Função para atualizar assetGroupId (para ser chamada do componente)
    const updateAssetGroupId = (value) => {
        globalFilters.assetGroupId.value = value
        // Salvar imediatamente após atualizar
        nextTick(() => {
            saveFiltersToStorage()
        })
    }

    // Configurar watchers para auto-salvar
    const setupAutoSave = () => {
        // Watch para mudanças nos filtros e salvar automaticamente
        watch(
            [
                globalFilters.searchQuery,
                globalFilters.searchOwner,
                globalFilters.customerId,
                globalFilters.chosenStatus,
                globalFilters.showFilters,
                globalFilters.assetTypeId,
                globalFilters.assetBrandId,
                globalFilters.assetGroupId,
                globalFilters.perPage,
                globalFilters.sortBy,
                globalFilters.sortDesc
            ],
            () => {
                // Usar nextTick para garantir que o valor foi completamente atualizado
                nextTick(() => {
                    saveFiltersToStorage()
                })
            },
            {deep: true}
        )
    }

    // Auto-save após montar (filtros já hidratados na inicialização do composable)
    onMounted(() => {
        setupAutoSave()
    })

    return {
        // Refs dos filtros
        searchQuery: globalFilters.searchQuery,
        searchOwner: globalFilters.searchOwner,
        customerId: globalFilters.customerId,
        chosenStatus: globalFilters.chosenStatus,
        showFilters: globalFilters.showFilters,
        assetTypeId: globalFilters.assetTypeId,
        assetBrandId: globalFilters.assetBrandId,
        assetGroupId: globalFilters.assetGroupId,
        perPage: globalFilters.perPage,
        sortBy: globalFilters.sortBy,
        sortDesc: globalFilters.sortDesc,

        // Funções utilitárias
        loadFiltersFromStorage,
        saveFiltersToStorage,
        clearFilters,
        setupAutoSave,
        updateSearchOwner,
        updateCustomerId,
        updateAssetGroupId
    }
}